import React from 'react';
import { LogIn, ShieldAlert, Award, TrendingUp, Truck, DollarSign, Users, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import machoLogo from '../assets/macho-logo.png';

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try { await login(email.trim(), password); }
    catch { setError('Invalid email or password'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-white to-slate-50 p-6" data-testid="login-page">
      <div className="grid md:grid-cols-2 gap-10 max-w-5xl w-full items-center">
        <div>
          <img
            src={machoLogo}
            alt="Macho Engineering Systems Pvt Ltd"
            className="h-14 mb-5"
            data-testid="login-macho-logo"
          />
          <div className="flex items-baseline gap-3 mb-6">
            <div className="text-3xl font-bold text-slate-900 leading-none tracking-tight" data-testid="login-app-name">
              Genba Pulse
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">
              SQPDCHI Manufacturing Board
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 leading-tight mb-4">
            Digital Lean Management for the Shop Floor
          </h1>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Track daily Safety, Quality, Productivity, Delivery, Cost, Human Development
            and Inventory across every team. Configure your own status colors, capture
            issue details, assign owners and run root-cause analyses — all from one board.
          </p>
          <form onSubmit={onSubmit} className="space-y-3 max-w-sm" data-testid="login-form">
            <input type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-slate-300" data-testid="login-email" />
            <input type="password" required placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-slate-300" data-testid="login-password" />
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button type="submit" size="lg" disabled={busy}
              className="bg-[#0090D8] hover:bg-[#0079B5] text-white h-12 px-8 text-base font-semibold shadow-md w-full">
              <LogIn className="h-5 w-5 mr-2" />{busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { k: 'S', label: 'Safety', color: '#ef4444', Icon: ShieldAlert },
              { k: 'Q', label: 'Quality', color: '#f59e0b', Icon: Award },
              { k: 'P', label: 'Productivity', color: '#3b82f6', Icon: TrendingUp },
              { k: 'D', label: 'Delivery', color: '#8b5cf6', Icon: Truck },
              { k: 'C', label: 'Cost', color: '#059669', Icon: DollarSign },
              { k: 'H', label: 'Human Dev.', color: '#0ea5e9', Icon: Users },
              { k: 'I', label: 'Inventory', color: '#ec4899', Icon: Package },
            ].map(({ k, label, color, Icon }) => (
              <div key={k} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-14 h-14 rounded-lg flex items-center justify-center text-white text-xl font-bold shadow-sm"
                  style={{ background: color }}
                >
                  {k}
                </div>
                <div className="flex items-center gap-1 text-[10px] font-medium text-slate-600 text-center">
                  <Icon className="h-3 w-3" />
                  <span>{label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {[
              'Configurable status colors and detail fields per category',
              'Instant issue creation with owner & target date',
              'Analytics with day / week / month trend charts',
              'A3, 5-Why, Fishbone, Pareto, PDCA, DMAIC & Kaizen templates',
              'Temporary / permanent countermeasure & closure tracking',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-green-100 border border-green-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                </div>
                <span className="text-sm text-slate-700">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
